import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Play, Rocket } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ARTICLES, getArticle } from "../data/blog";

const pick = (obj, lang) => obj[lang] || obj.fr;

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return iso;
  }
};

const Shell = ({ children, onHome }) => (
  <div className="app-bg noise-overlay relative min-h-[100dvh] w-full">
    <div className="relative z-10 mx-auto w-full max-w-3xl px-5 py-8 sm:px-8 sm:py-12">
      <div className="flex items-center gap-3">
        <button
          onClick={onHome}
          data-testid="blog-home-button"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-transform hover:scale-105"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-white/60">
          <Rocket size={16} className="text-[#f093fb]" /> Destin Express
        </div>
      </div>
      {children}
    </div>
  </div>
);

// Liste des articles
export const BlogScreen = ({ onHome, onOpen }) => {
  const { lang, t } = useLang();
  return (
    <Shell onHome={onHome}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-8"
        data-testid="blog-screen"
      >
        <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
          {t("blogTitle")}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-base">
          {t("blogSubtitle")}
        </p>
      </motion.div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        {ARTICLES.map((a, i) => (
          <motion.button
            key={a.slug}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
            onClick={() => onOpen(a.slug)}
            data-testid={`blog-card-${a.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] text-left transition-transform hover:-translate-y-1"
          >
            <div className="aspect-[16/9] w-full overflow-hidden">
              <img
                src={a.cover}
                alt={pick(a.title, lang)}
                loading="lazy"
                className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <div className="flex items-center gap-3 text-[11px] text-white/40">
                <span>{formatDate(a.date, lang)}</span>
                <span className="flex items-center gap-1">
                  <Clock size={11} /> {a.readMin} {t("blogRead")}
                </span>
              </div>
              <h2 className="mt-2 font-display text-lg font-bold leading-snug text-white">
                {pick(a.title, lang)}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-white/55">{pick(a.excerpt, lang)}</p>
              <span className="mt-3 text-sm font-bold text-[#f093fb]">{t("blogReadMore")} →</span>
            </div>
          </motion.button>
        ))}
      </div>
    </Shell>
  );
};

// Article unique
export const BlogArticleScreen = ({ slug, onHome, onBackToBlog, onPlay }) => {
  const { lang, t } = useLang();
  const a = getArticle(slug);

  if (!a) {
    return (
      <Shell onHome={onHome}>
        <div className="mt-12 text-center text-white/60" data-testid="blog-not-found">
          {t("blogNotFound")}
          <div className="mt-4">
            <button onClick={onBackToBlog} className="text-sm font-bold text-[#f093fb]">
              ← {t("blogTitle")}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  const sections = pick(a.sections, lang);

  return (
    <Shell onHome={onHome}>
      <motion.article
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-8"
        data-testid="blog-article"
      >
        <button
          onClick={onBackToBlog}
          data-testid="blog-back-to-list"
          className="mb-6 text-sm font-semibold text-white/50 transition-colors hover:text-white"
        >
          ← {t("blogTitle")}
        </button>

        <div className="flex items-center gap-3 text-[11px] text-white/40">
          <span>{formatDate(a.date, lang)}</span>
          <span className="flex items-center gap-1">
            <Clock size={11} /> {a.readMin} {t("blogRead")}
          </span>
        </div>
        <h1 className="mt-2 font-display text-3xl font-black leading-tight text-white sm:text-4xl">
          {pick(a.title, lang)}
        </h1>

        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
          <img src={a.cover} alt={pick(a.title, lang)} className="aspect-[16/9] w-full object-cover" />
        </div>

        <div className="mt-8 space-y-8">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-xl font-bold text-white">{s.h}</h2>
              {s.p.map((para, j) => (
                <p key={j} className="mt-3 text-[15px] leading-relaxed text-white/70">
                  {para}
                </p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-[#f093fb]/30 bg-gradient-to-br from-[#f093fb]/10 to-[#f5576c]/5 p-6 text-center">
          <p className="font-display text-lg font-bold text-white">{t("blogCtaTitle")}</p>
          <button
            onClick={onPlay}
            data-testid="blog-play-cta"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] px-6 py-3 font-display font-bold text-white transition-transform active:scale-95"
          >
            <Play size={18} /> {t("blogCtaButton")}
          </button>
        </div>
      </motion.article>
    </Shell>
  );
};
